import React from "react";

interface ProjectDetailModalProps {
  project: {
    emoji: string;
    title: string;
    difficulty: string;
    age: string;
    description: string;
    category: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, isOpen, onClose }) => {
  if (!isOpen) return null;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstimatedTime = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return '1-2 hours';
      case 'Medium': return '3-5 hours';
      case 'Hard': return '6-10 hours';
      default: return '2-4 hours';
    }
  };

  const getRequiredComponents = (category: string) => {
    const baseComponents = ['Arduino Uno', 'Breadboard', 'Jumper wires', 'Resistors'];
    
    switch (category) {
      case 'LEDs':
        return [...baseComponents, 'LEDs (various colors)', 'RGB LED strip', 'Transistors'];
      case 'Music':
        return [...baseComponents, 'Buzzer/Speaker', 'Potentiometer', 'Buttons'];
      case 'Sensors':
        return [...baseComponents, 'Temperature sensor', 'Light sensor', 'Display (LCD/OLED)'];
      case 'Robotics':
        return [...baseComponents, 'Motors', 'Motor driver', 'Wheels', 'Chassis', 'Battery pack'];
      case 'IoT':
        return [...baseComponents, 'WiFi module (ESP32)', 'Relay module', 'Web server components'];
      case 'Games':
        return [...baseComponents, 'Joystick', 'Buttons', 'Display', 'Speaker'];
      case 'Security':
        return [...baseComponents, 'Keypad', 'Servo motor', 'Camera module', 'PIR sensor'];
      case 'Science':
        return [...baseComponents, 'Various sensors', 'Data logger', 'Microscope camera', 'Analysis software'];
      default:
        return baseComponents;
    }
  };

  const getLearningOutcomes = (category: string) => {
    switch (category) {
      case 'LEDs':
        return ['Basic electronics', 'Programming logic', 'Color theory', 'Circuit design'];
      case 'Music':
        return ['Sound generation', 'Frequency control', 'User interface design', 'Musical theory'];
      case 'Sensors':
        return ['Data collection', 'Signal processing', 'Display programming', 'Environmental monitoring'];
      case 'Robotics':
        return ['Motor control', 'Sensor integration', 'Path planning', 'Autonomous systems'];
      case 'IoT':
        return ['Network programming', 'Web development', 'Data transmission', 'Remote control'];
      case 'Games':
        return ['User interaction', 'Game logic', 'Input handling', 'Score systems'];
      case 'Security':
        return ['Access control', 'Sensor integration', 'Alarm systems', 'User authentication'];
      case 'Science':
        return ['Data analysis', 'Scientific measurement', 'Hypothesis testing', 'Research methods'];
      default:
        return ['Problem solving', 'Critical thinking', 'Technical skills', 'Creativity'];
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-4xl">{project.emoji}</div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Lexend, sans-serif'}}>{project.title}</h2>
                  <p className="text-gray-600" style={{fontFamily: 'Lexend, sans-serif'}}>{project.description}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Project Info */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>Project Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600" style={{fontFamily: 'Lexend, sans-serif'}}>Difficulty:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getDifficultyColor(project.difficulty)}`}>
                        {project.difficulty}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Age Range:</span>
                      <span className="font-medium">{project.age}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category:</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                        {project.category}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Estimated Time:</span>
                      <span className="font-medium">{getEstimatedTime(project.difficulty)}</span>
                    </div>
                  </div>
                </div>

                {/* Learning Outcomes */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">What You'll Learn</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {getLearningOutcomes(project.category).map((outcome, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="text-green-500">✓</span>
                        <span className="text-sm text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Requirements & Steps */}
              <div className="space-y-6">
                {/* Required Components */}
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Required Components</h3>
                  <div className="space-y-2">
                    {getRequiredComponents(project.category).map((component, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="text-yellow-500">🔧</span>
                        <span className="text-sm text-gray-700">{component}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Project Steps */}
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Project Steps</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                      <span className="text-sm text-gray-700">Gather all required components</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                      <span className="text-sm text-gray-700">Set up your Arduino/Raspberry Pi</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                      <span className="text-sm text-gray-700">Build the circuit following the diagram</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                      <span className="text-sm text-gray-700">Write and upload the code</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                      <span className="text-sm text-gray-700">Test and troubleshoot your project</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">6</span>
                      <span className="text-sm text-gray-700">Customize and add your own features!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105">
                📚 Get Detailed Guide
              </button>
              <button className="bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105">
                🛒 Add to Starter Pack
              </button>
              <button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105">
                💬 Ask Questions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;
